import { Link, useSearchParams } from 'react-router';
import type { Resource } from '@bekebi/contracts/api';
import { Button, buttonClass } from '../../components/button';
import { EmptyState, ErrorState, LoadingState } from '../../components/states';
import { useHasPermission } from '../../session/current-user';
import { useResources } from './api';

/**
 * Infrastructure overview (/app/infrastructure): the resources Bekebi's services depend on, from
 * GET /api/infrastructure/resources (cursor pagination). Each shows its kind, external reference,
 * parent and how many services use it (the API's own count). The inventory holds no status or
 * health: service health lives on each service. The kind filter applies to loaded resources.
 */
export function InfrastructureOverview() {
  const resources = useResources();
  const canManage = useHasPermission('infrastructure.manage');
  const [params, setParams] = useSearchParams();
  const kindFilter = params.get('kind') ?? '';

  const all = resources.data?.pages.flatMap((p) => p.items) ?? [];
  const byId = new Map(all.map((r) => [r.id, r]));
  const kinds = [...new Set(all.map((r) => r.kind))].sort();
  const shown = kindFilter ? all.filter((r) => r.kind === kindFilter) : all;

  const addLink = canManage ? (
    <Link to="/app/infrastructure/new" className={buttonClass('primary')}>
      Add resource
    </Link>
  ) : null;

  return (
    <div className="max-w-5xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1>Infrastructure</h1>
        {addLink}
      </div>
      <p className="mt-1 text-sm text-muted">
        The systems Bekebi&rsquo;s services run on, and which services use them. This is an
        inventory: provider administration happens outside the portal.
      </p>

      {kinds.length > 1 ? (
        <div className="mt-6">
          <label htmlFor="filter-kind" className="block text-xs font-medium text-muted">
            Kind
          </label>
          <select
            id="filter-kind"
            value={kindFilter}
            onChange={(e) => {
              const next = new URLSearchParams(params);
              if (e.target.value) next.set('kind', e.target.value);
              else next.delete('kind');
              setParams(next, { replace: true });
            }}
            className="mt-1 min-h-10 rounded-md border border-line-strong bg-surface px-3 text-sm"
          >
            <option value="">All kinds</option>
            {kinds.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      <div className="mt-6">
        {resources.isPending ? (
          <LoadingState label="Loading resources…" />
        ) : resources.isError ? (
          <ErrorState
            title="Resources could not be loaded"
            message={resources.error.message}
            onRetry={() => void resources.refetch()}
          />
        ) : all.length === 0 ? (
          <EmptyState
            title="No infrastructure resources yet."
            description="No resources are recorded in the portal."
            action={addLink}
          />
        ) : shown.length === 0 ? (
          <EmptyState title="No resources of this kind." />
        ) : (
          <>
            <div
              aria-hidden="true"
              className="hidden grid-cols-[1.6fr_1fr_1.2fr_1fr] gap-4 px-4 pb-2 text-xs font-medium uppercase tracking-wide text-muted md:grid"
            >
              <span>Resource</span>
              <span>Kind</span>
              <span>External reference</span>
              <span>Used by</span>
            </div>
            <ul aria-label="Resources" className="space-y-2">
              {shown.map((resource) => (
                <li key={resource.id}>
                  <ResourceRow
                    resource={resource}
                    parent={
                      resource.parentResourceId ? byId.get(resource.parentResourceId) : undefined
                    }
                  />
                </li>
              ))}
            </ul>
            {resources.hasNextPage ? (
              <div className="mt-4">
                <Button
                  variant="secondary"
                  onClick={() => void resources.fetchNextPage()}
                  disabled={resources.isFetchingNextPage}
                >
                  {resources.isFetchingNextPage ? 'Loading…' : 'Show more resources'}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

export function usageLabel(count: number): string {
  if (count === 0) return 'No services';
  if (count === 1) return '1 service';
  return `${count} services (shared)`;
}

function ResourceRow({ resource, parent }: { resource: Resource; parent: Resource | undefined }) {
  return (
    <article className="grid gap-2 rounded-lg border border-line bg-surface p-4 text-sm md:grid-cols-[1.6fr_1fr_1.2fr_1fr] md:items-center md:gap-4">
      <div className="min-w-0">
        <h2 className="text-sm font-semibold">
          <Link
            to={`/app/infrastructure/${resource.id}`}
            className="break-words text-fg underline-offset-4 hover:underline"
          >
            {resource.name}
          </Link>
        </h2>
        {resource.parentResourceId ? (
          <p className="text-xs text-muted">Part of {parent ? parent.name : 'another resource'}</p>
        ) : null}
      </div>
      <div>
        <span className="text-muted md:hidden">Kind: </span>
        <span className="font-mono text-xs text-fg-2">{resource.kind}</span>
      </div>
      <div className="min-w-0 break-all">
        <span className="text-muted md:hidden">External reference: </span>
        {resource.externalRef ? (
          <span className="font-mono text-xs text-fg-2">{resource.externalRef}</span>
        ) : (
          <span className="text-muted">None recorded</span>
        )}
      </div>
      <div className="text-fg-2">
        <span className="text-muted md:hidden">Used by: </span>
        {usageLabel(resource.usage.linkedServiceCount)}
      </div>
    </article>
  );
}
